import { agentWritesApi } from '../../api/agents/agent-writes'
import type {
  InFlightAgentActionProposal,
  InFlightTagWriteProposal
} from '../../slices/agent-orchestrator-slice'
import {
  buildTagWriteEntriesFromToolInput,
  scheduleHydratePendingAgentWrites
} from '../cypher-write-pending'
import {
  parseSettledProposeOutput,
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionApplyOutcome,
  type AgentActionCompleteHydration,
  type AgentActionLifecycleCapability,
  type AgentActionRejectOutcome
} from './lifecycle-registry'

export const PROPOSE_TAG_WRITE_TOOL_NAME = 'propose_tag_write'

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

function readNumber(record: Record<string, unknown>, key: string): number | undefined {
  const value = record[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/** Parse the legacy pending envelope returned when the blocking wait times out. */
export function readTagWriteProposeOutput(
  parentToolUseId: string,
  output: unknown
): AgentActionCompleteHydration | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return null
  const body = output as Record<string, unknown>
  if (body.pending !== true) return null
  const planId = readString(body, 'plan_id')
  if (!planId || !Array.isArray(body.results)) return null
  const results: AgentActionCompleteHydration['results'] = []
  for (const raw of body.results) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const row = raw as Record<string, unknown>
    const toolUseId = readString(row, 'tool_use_id')
    const writeId = readString(row, 'write_id')
    if (!toolUseId || !writeId) continue
    results.push({ toolUseId, writeId, index: readNumber(row, 'index') })
  }
  return { parentToolUseId, planId, results }
}

/** Normalize proxy apply metadata for the settled tag-write card. */
export function readTagWriteApplyMeta(
  result: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!result) return {}
  const eventId = readString(result, 'event_id') ?? readString(result, 'eventId')
  const priorValue = result.prior_value ?? result.priorValue
  return {
    ...(priorValue !== undefined ? { priorValue } : {}),
    ...(eventId ? { reversibleEventId: eventId } : {})
  }
}

function sanitizeSummary(value: string): string {
  return value.replace(/"/g, '').trim()
}

export const tagWriteLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'tag_write',
  toolName: PROPOSE_TAG_WRITE_TOOL_NAME,

  parseToolStart(toolUseId, input, startedAtMs) {
    return buildTagWriteEntriesFromToolInput(toolUseId, input, startedAtMs)
  },

  parseToolComplete(toolUseId, output) {
    return readTagWriteProposeOutput(toolUseId, output)
  },

  parseSettledToolComplete(output) {
    return parseSettledProposeOutput(output)
  },

  scheduleHydratePending(dispatch, conversationId) {
    scheduleHydratePendingAgentWrites(dispatch, conversationId)
  },

  apply(toolUseId) {
    return async (dispatch): Promise<AgentActionApplyOutcome> => {
      try {
        const response = await dispatch(
          agentWritesApi.endpoints.applyCypherWrite.initiate({
            toolUseId,
            graphAffecting: false
          })
        ).unwrap()
        if (response.ok) {
          return { kind: 'success', settledMeta: readTagWriteApplyMeta(response.result) }
        }
        return { kind: 'apply_failed', error: response.error ?? 'Tag write failed.' }
      } catch (error) {
        const refused = readStructuredMutationError(error)
        if (refused) return { kind: 'apply_failed', error: refused.error }
        return {
          kind: 'transport_error',
          error: error instanceof Error ? error.message : 'Tag write failed.'
        }
      }
    }
  },

  reject(toolUseId, reason) {
    return async (dispatch): Promise<AgentActionRejectOutcome> => {
      try {
        const response = await dispatch(
          agentWritesApi.endpoints.rejectCypherWrite.initiate({ toolUseId, reason })
        ).unwrap()
        if (response.ok) {
          return {
            kind: 'success',
            settledMeta: { rejectionReason: response.reason ?? reason }
          }
        }
        return { kind: 'reject_failed', error: response.error ?? 'Reject failed.' }
      } catch (error) {
        return {
          kind: 'transport_error',
          error: error instanceof Error ? error.message : 'Reject failed.'
        }
      }
    }
  },

  buildResumeRowLine(proposal: InFlightAgentActionProposal, displayNumber) {
    if (proposal.kind !== 'tag_write') {
      return `${displayNumber}. UNDECIDED — "${sanitizeSummary(proposal.summary)}".`
    }
    const tagWrite = proposal as InFlightTagWriteProposal
    const summary = sanitizeSummary(tagWrite.summary || `Write ${tagWrite.tagId}`)
    const eventRef = tagWrite.reversibleEventId
      ? `; reversible event ${tagWrite.reversibleEventId}`
      : ''
    if (tagWrite.terminalState?.kind === 'applied') {
      return `${displayNumber}. APPLIED — "${summary}"${eventRef}.`
    }
    if (tagWrite.terminalState?.kind === 'apply_failed') {
      return `${displayNumber}. APPLY_FAILED — "${summary}"; error: ${tagWrite.terminalState.error}.`
    }
    if (tagWrite.terminalState?.kind === 'rejected') {
      return `${displayNumber}. REJECTED — "${summary}"; reason: "${tagWrite.rejectionReason ?? 'rejected by user'}".`
    }
    return `${displayNumber}. UNDECIDED — "${summary}".`
  }
}

registerAgentActionLifecycle(tagWriteLifecycleCapability)
